<template lang="pug">
v-row(no-gutters)
  v-col.pa-10(cols="3" xl="2")
    .sticky.top-24.white
      .text-md.font-semibold.neutral500--text.px-4.py-2.round-md.mb-2.clickable(@click="$router.push('/admin/clinic/settings/business-details')") {{ $t('business_details') }}
      .text-md.font-semibold.neutral200.px-4.py-2.round-md.mb-2 {{ $t('tax_settings') }}
      .text-md.font-semibold.neutral500--text.px-4.py-2.round-md.mb-2.clickable(@click="$router.push('/admin/clinic/settings/e-invoice')") e-Invoice settings

  v-col.neutral200(cols="9" xl="10")
    v-container.pa-8.h-screen(fluid style="width:85%")
      .d-flex.align-center.mb-6
        v-btn.mr-4(icon small)
          v-icon(color="primary" @click="back") mdi-arrow-left
        .text-2xl.font-semibold {{ $t('tax_settings') }}
      
      // Auto-apply rate
      v-card.round-xl.pa-6.mb-4(outlined)
        div.d-flex.justify-space-between
          div
            .text-md.font-semibold.neutral700--text Apply default tax rate to all incoming invoices
            .text-sm.neutral700--text.mt-2 Enable this setting to automatically apply the default tax rate to every incoming invoice.
          div
            y-switch.mb-n3.mt-2(v-model="defaultTaxEnabled" @change="updateDefaultTaxRate(defaultTaxEnabled)")
        v-row.mt-3(v-if="defaultTaxEnabled")
          v-col(cols="6")
            div.text-sm.font-semibold.neutral500--text Choose a tax rate to apply
            select-tax-rate-dropdown.mt-2(v-model="defaultTaxRate" :error="false" @selected="updateDefaultTaxRate(defaultTaxEnabled, $event)" @create="addTax($event)")
            div.d-flex.justify-center(v-if="isFetching")
              v-progress-linear(:width="3" rounded color="primary" indeterminate)

      v-card.round-xl.pa-6.mb-4(outlined)
        .h8.mb-1 Tax setup
        .text-sm.font-medium.mb-6 Add, edit, or remove your tax

        v-row.pb-2.pb-md-0(no-gutters)
          v-col(cols="12" md="6")
            v-tabs.mb-2.mb-md-0(color="basic700" height="44")
              v-tab.text-sm.font-normal.text-none(active-class="font-semibold") All

          v-col(cols="12" md="6" align="end")
            .d-flex.align-center
              v-spacer
              v-btn(icon color="neutral500" @click="onClickSearch")
                v-icon(size="20") $yzi-search-sm
              v-text-field(v-if="showSearchField" v-model="searchText" @blur="onBlurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
              y-button.ml-2(label="Add tax" @click="addTax" size="sm" outlined)
        v-divider.mb-6
        
        .data-table
          v-data-table.mt-sm-4.pb-6(:headers="headers" :items="taxRates" item-key="id" hide-default-footer :loading="isLoading" :disable-sort="isMobileScreen" :header-props="{ sortIcon: '$yzi-triangle' }")
            //- name
            template(v-slot:item.name="{ item }")
              .text-sm.neutral700--text {{ item.name }}
            //- rate
            template(v-slot:item.rate="{ item }")
              .text-sm.neutral700--text {{ (item.rate / 100) | percentage }}
            //- description
            template(v-slot:item.description="{ item }")
              .text-sm.neutral700--text {{ item.description }}
            //- actions
            template(v-slot:item.actions="{ item, index }")
              div(@click.stop)
                v-menu(offset-y :top="menuPositionTop(index)" left attach rounded="lg")
                  template(#activator="{ on }")
                    v-btn(icon small v-on="on")
                      v-icon mdi-dots-horizontal
                  v-list.py-0
                    v-list-item(@click="editTax(item)")
                      v-list-item-title.text-sm.basic800--text Edit tax
                    v-divider
                    v-list-item(@click="deleteTax(item)")
                      v-list-item-title.text-sm.danger500--text Delete tax

  .dialogs
    add-edit-tax-dialog(v-if="showTaxDialog" :open.sync="showTaxDialog" :tax="selectedTax" :inputName="inputName" @onUpdated="onUpdated" @onAdded="reload")
    delete-tax-dialog(v-if="showDeleteDialog" :open.sync="showDeleteDialog" :tax="selectedTax" @onDeleted="onDeleted")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import SelectTaxRateDropdown from '@/components/clinic/settings/SelectTaxRateDropdown.vue'
import AddEditTaxDialog from '@/components/clinic/dialogs/AddEditTaxDialog.vue'
import DeleteTaxDialog from '@/components/clinic/dialogs/DeleteTaxDialog.vue'

export default {
  name: 'TaxSettings',
  components: { SelectTaxRateDropdown, AddEditTaxDialog, DeleteTaxDialog },
  data: () => ({
    taxRates: [],
    isFetching: false,
    isLoading: false,
    showTaxDialog: false,
    defaultTaxEnabled: false,
    defaultTaxRate: null,
    selectedTax: null,
    inputName: '',
    showDeleteDialog: false,
    showSearchField: false,
    searchText: null,
    headers: [
      { text: 'Name', value: 'name', enable: true },
      { text: 'Rate', value: 'rate', enable: true },
      { text: 'Description', value: 'description', enable: true },
      { text: '', value: 'actions', enable: true, class: 'text-end', width: 60, sortable: false },
    ]
  }),
  mounted () {
    this.defaultTaxRate = this.company.default_tax_rate
    this.defaultTaxEnabled = !!this.company.default_tax_rate
    this.fetchTaxRates()
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
  },
  methods: {
    ...mapActions(['showMessage', 'updateCompany']),
    reload () {
      this.fetchTaxRates()
    },
    queryChanged: debounce(function () {
      this.fetchTaxRates()
    }, 750),
    fetchDefaultTaxRate () {
      this.isFetching = true
      this.$axios.get(`/companies/${this.company.id}`)
        .then(resp => {
          this.defaultTaxRate = resp.data.default_tax_rate
          this.defaultTaxEnabled = !!resp.data.default_tax_rate
          this.updateCompany(resp.data)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    updateDefaultTaxRate (status, selectedItem=null) {
      if ((!status && this.defaultTaxRate) || (status && selectedItem)) {
        const selectedTaxId = selectedItem?.id ? selectedItem.id : null
        this.$axios.patch(`/companies/${this.company.id}`, { default_tax_rate: selectedTaxId })
          .then(() => {
            this.fetchDefaultTaxRate()
            this.showMessage(['success', this.$t('success_message.successfully_updated')])
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
      }
    },
    onClickSearch() {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true
      }
    },
    onBlurSearch() {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    fetchTaxRates () {
      this.isLoading = true
      const params = {
        company: this.company.id, 
        page_size: -1,
        search: this.searchText,
      }
      this.$axios.get('/tax_rates', { params })
        .then(({ data }) => {
          if (data && data.results) {
            this.taxRates = data.results
          } else {
            this.taxRates = []
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    editTax (tax) {
      this.selectedTax = tax
      this.showTaxDialog = true
    },
    deleteTax (tax) {
      this.selectedTax = tax
      this.showDeleteDialog = true
    },
    addTax (inputName='') {
      this.selectedTax = null
      this.showTaxDialog = true
      this.inputName = inputName
    },
    onUpdated (tax) {
      const index = this.taxRates.findIndex(i => i.id === tax.id)
      if (index >= 0) {
        const index = this.taxRates.findIndex(i => i.id === tax.id)
        this.taxRates.splice(index, 1, tax)
      }
      if (this.defaultTaxRate?.id === tax.id) {
        this.defaultTaxRate = tax
      }
    },
    onDeleted () {
      const index = this.taxRates.findIndex(i => i.id === this.selectedTax.id)
      this.taxRates.splice(index, 1)
      if (this.defaultTaxRate?.id === this.selectedTax.id) {
        this.fetchDefaultTaxRate()
      }
    },
    menuPositionTop (index) {
      let position = index + 1
      if (position > 0) {
        let start = this.taxRates.length - 2
        if (position > start) {
          return true
        }
      }
      return false
    },
    back () {
      this.$router.push('/admin/clinic/settings')
    },
  }
}
</script>